import type { Currency } from '@ps/web-core-enums';
import type { Error } from '@ps/web-shared-components';
import React from 'react';

import CurrencyAmount from '@swf/components/CurrencyAmount/CurrencyAmount';

import type { Rate } from '../types/Cashier';
import { convertAmountByRate, getConversionRate, isZeroExponentCurrency } from '../utils/cashier';

import { TXTWCL_Deposits_must_be_between_N0_an_ELL } from './minLimit.messages.json';

export const createMinLimitValidator =
  (minDepositLimit: number, maxDepositLimit: number, rates: Rate[], balanceCurrency: Currency) =>
  (value: string, allValues: { deposit: { currency: Currency } }): Error => {
    const {
      deposit: { currency }
    } = allValues;

    const currencyRate = getConversionRate(rates, currency);
    const showConversion = currency !== balanceCurrency;
    const convertedAmount = convertAmountByRate(Number(value), currencyRate, balanceCurrency);
    const valid = convertedAmount >= minDepositLimit;
    const isBalanceCurrencyZeroExponent = isZeroExponentCurrency(balanceCurrency);
    const isCurrencyZeroExponent = isZeroExponentCurrency(currency);

    const minLimit = (
      <CurrencyAmount
        value={minDepositLimit}
        isZeroExponentCurrency={isBalanceCurrencyZeroExponent}
        currency={balanceCurrency}
        currencyDisplay="code"
        dollarValue
      >
        {(parts) => {
          const joinedParts = parts.map((part) => part.value).join('');

          if (showConversion) {
            return (
              <>
                <CurrencyAmount
                  value={convertAmountByRate(Number(minDepositLimit), currencyRate, currency, true)}
                  isZeroExponentCurrency={isCurrencyZeroExponent}
                  currency={currency}
                  currencyDisplay="code"
                  dollarValue
                />{' '}
                ({joinedParts})
              </>
            );
          }

          return joinedParts;
        }}
      </CurrencyAmount>
    );

    const maxLimit = (
      <CurrencyAmount
        value={maxDepositLimit}
        isZeroExponentCurrency={isBalanceCurrencyZeroExponent}
        currency={balanceCurrency}
        currencyDisplay="code"
        dollarValue
      >
        {(parts) => {
          const joinedParts = parts.map((part) => part.value).join('');

          if (showConversion) {
            return (
              <>
                <CurrencyAmount
                  value={convertAmountByRate(Number(maxDepositLimit), currencyRate, currency, true)}
                  isZeroExponentCurrency={isCurrencyZeroExponent}
                  currency={currency}
                  currencyDisplay="code"
                  dollarValue
                />{' '}
                ({joinedParts})
              </>
            );
          }

          return joinedParts;
        }}
      </CurrencyAmount>
    );

    return {
      valid,
      message: {
        ...TXTWCL_Deposits_must_be_between_N0_an_ELL,
        values: { 0: minLimit, 1: maxLimit }
      }
    };
  };
